import { useMemo } from 'react';
import type { IListedDashboard } from '@gooddata/sdk-model';
import { useSelector } from 'react-redux';
import { useFeatureFlagService, useLocalizeText } from '@mews/framework';
import {
    useRepositoryMutation,
    useRepositorySuspenseQuery,
    useRepositoryInfiniteQuery,
    EmployeeLoadTypeEnum,
} from '@mews-commander/data-access';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { getSelectedServiceId } from '@mews-commander/legacy-entities/src/ducks/serviceSelect';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { getEnterpriseDefaultServiceId } from '@mews-commander/legacy-entities/src/selectors/enterpriseServices';
import { isCurrentEnterprisePortfolio } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector } from '@mews-commander/utils';
import { GDProduct } from '@mews-framework/good-data';
import { DASHBOARD_IDS_WITH_ENTERPRISE_FILTER, DASHBOARD_IDS_WITH_SERVICE_FILTER } from './const';
import { convertDashboards, getServiceName, removeExcludedDashboards } from './utils';
import type { Filter, FilterContext } from './types';

const product = GDProduct.MewsBI;

export function useCreateFilterContext() {
    return useRepositoryMutation('goodData', 'createFilterContext', {
        transformParams: (params) => ({ product, ...params }),
    });
}

export function useGetDashboard(dashboardId: string) {
    return useRepositorySuspenseQuery('goodDataDashboards', 'get', { dashboardId, product });
}

export function useGetDashboards() {
    return useRepositorySuspenseQuery('goodDataDashboards', 'getAll', { product });
}

export function useGetDashboardPermissions(dashboards: IListedDashboard[]) {
    return useRepositorySuspenseQuery('goodDataDashboards', 'getPermissions', { product, dashboards });
}

export function useDashboards() {
    const featureFlagService = useFeatureFlagService();
    const localizeText = useLocalizeText();
    const { data: currentUser } = useGetCurrentUser();
    const { data: goodDataDashboards } = useGetDashboards();

    const validGDDashboards = useMemo(
        () => removeExcludedDashboards(goodDataDashboards, featureFlagService),
        [goodDataDashboards, featureFlagService]
    );

    const { data: dashboardPermissions } = useGetDashboardPermissions(validGDDashboards);
    const dashboards = convertDashboards(validGDDashboards, currentUser, dashboardPermissions, localizeText);

    return dashboards;
}

export function useDuplicateDashboard() {
    return useRepositoryMutation('goodDataDashboards', 'duplicate', {
        transformParams: (params) => ({ product, ...params }),
    });
}

export function useGetCurrentUser() {
    return useRepositorySuspenseQuery('goodDataCurrentUser', 'get', { product });
}

export function useGetCurrentUserPermissions() {
    return useRepositorySuspenseQuery('goodDataCurrentUser', 'getPermissions', { product });
}

interface useDeleteDashboardProps {
    onSuccess: () => void;
}

export function useDeleteDashboard({ onSuccess }: useDeleteDashboardProps) {
    return useRepositoryMutation('goodDataDashboards', 'delete', {
        transformParams: (params) => ({ product, ...params }),
        invalidateQueries: {
            goodDataDashboards: ['getAll', 'getPermissions'],
        },
        onSuccess,
    });
}

const createFilter = (identifier: string, values: string[]): Filter => ({
    positiveAttributeFilter: {
        displayForm: { identifier },
        in: values,
    },
});

const createEnterpriseFilter = (enterpriseName: string): Filter =>
    createFilter('enterprise_enterpriseName', [enterpriseName]);

const createServiceFilter = (serviceName: string | undefined): Filter =>
    createFilter('service_name', serviceName ? [serviceName] : []);

const createFilterContext = (
    enterpriseName: string,
    serviceName: string | undefined,
    dashboardId: string
): FilterContext | undefined => {
    const filters: Filter[] = [];

    const needsEnterpriseFilter = DASHBOARD_IDS_WITH_ENTERPRISE_FILTER.includes(dashboardId);
    const needsServiceFilter = DASHBOARD_IDS_WITH_SERVICE_FILTER.includes(dashboardId);

    if (!needsEnterpriseFilter && !needsServiceFilter) return undefined;

    if (needsEnterpriseFilter) filters.push(createEnterpriseFilter(enterpriseName));
    if (needsServiceFilter) filters.push(createServiceFilter(serviceName));

    return { filters };
};

export const useEnterpriseCustomFilterContext = (dashboardId: string): FilterContext | undefined => {
    const { name: enterpriseName, id: enterpriseId } = useCurrentEnterprise();

    const isPortfolio = useSelector(isCurrentEnterprisePortfolio);
    const currentServiceId: string | null = useCommanderSelector(getSelectedServiceId);
    const defaultServiceId: string = useCommanderSelector((state) =>
        getEnterpriseDefaultServiceId(state, { enterpriseId })
    );

    const preSelectId = currentServiceId ?? defaultServiceId;
    const preSelectService = useCommanderSelector((state) => getService(state, { id: preSelectId }));
    const serviceName = getServiceName(preSelectService);

    if (isPortfolio) return undefined;

    return createFilterContext(enterpriseName, serviceName, dashboardId);
};

const PAGE_SIZE = 100;

export const useUserProfiles = () =>
    useRepositoryInfiniteQuery(
        'userProfiles',
        'getAll',
        ({ pageParam }) => ({
            employeeLoadType: EmployeeLoadTypeEnum.PropertyEmployees,
            pagination: {
                pageSize: PAGE_SIZE,
                pageIndex: pageParam,
            },
        }),
        {
            select: (queryData) => queryData?.pages?.flatMap((page) => page.data),
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) =>
                lastPage.totalRowCount < PAGE_SIZE * allPages.length ? undefined : allPages.length,
        }
    );
